import React, { useState, useEffect } from 'react';
import { X, FileText, ScrollText, Landmark, Loader2, ArrowLeft } from 'lucide-react';

// 🟢 MATCHED PATTERN: Explicitly matching the signature of your other modals
interface ChurchDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userData: any;
  shopId: string | number;
}

export const ChurchDocumentsModal: React.FC<ChurchDocumentsModalProps> = ({ isOpen, onClose, userData, shopId }) => {
  if (!isOpen) return null;

  const [activeHtmlReport, setActiveHtmlReport] = useState<string | null>(null);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingTab, setBookingTab] = useState<'current' | 'hall' | 'boardroom'>('current');
  const [bookingsList, setBookingsList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form States for Hall Booking
  const [hallDate, setHallDate] = useState('');
  const [hallAttendance, setHallAttendance] = useState('');
  const [hallPurpose, setHallPurpose] = useState('');

  // Form States for Boardroom Booking
  const [boardroomDate, setBoardroomDate] = useState('');
  const [boardroomStartTime, setBoardroomStartTime] = useState('');
  const [boardroomEndTime, setBoardroomEndTime] = useState('');
  const [boardroomAgenda, setBoardroomAgenda] = useState('');

  const docs = [
    { title: 'Wedding Booking Form', desc: 'Schedule counseling and marriage liturgy', icon: HeartIcon },
    { title: 'Church Hall & Boardroom Booking', desc: 'Hire main hall or meeting rooms for events', icon: Landmark },
    { title: 'Funeral Service Planning', desc: 'Coordinate mass schedules and family support', icon: ScrollText },
    { title: 'Your Givings from the past week', desc: 'The Past Week Givings, Offertory & Tithes', icon: ScrollText }
  ];

  // Fetch current upcoming reservations from backend
  const fetchCurrentBookings = async () => {
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-facility-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId || userData?.shop_id || 68 })
      });
      const data = await response.json();
      setBookingsList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed fetching space schedule matrix:", err);
    }
  };

  // Trigger schedule loader whenever the 'current' listings tab mounts
  useEffect(() => {
    if (isBookingModalOpen && bookingTab === 'current') {
      fetchCurrentBookings();
    }
  }, [isBookingModalOpen, bookingTab]);

  const handleViewTithesReport = async () => {
    setLoadingReport(true);
    try {

      const activeShopId = userData?.shop_id || shopId;

      // 2. Strict Safety Gate: Throw an alert if no valid session context is resolved
      if (!activeShopId) {
        alert("Session validation error: Could not identify your church membership profile.");
        return;
      }

      const response = await fetch('https://n8n.tenear.com/webhook/church-member-access-doc', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // 🟢 FIXED PAYLOAD: Explicitly uses your component props variable naming
        body: JSON.stringify({ 
          shop_id: activeShopId,
          action: 'fetch_weekly_report'
        })
      });
      
      const data = await response.json();
      
      if (data && data.html) {
        setActiveHtmlReport(data.html);
      } else if (typeof data === 'string' && data.includes('<!DOCTYPE html>')) {
        setActiveHtmlReport(data);
      } else {
        alert("Report generated but failed to parse clean HTML document formatting.");
      }
    } catch (err) {
      console.error("Failed to stream collection ledger:", err);
      alert("System connection timeout. Could not synchronize church financial ledger records.");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleBookingSubmit = async (e: React.FormEvent, facilityType: 'Hall' | 'Boardroom') => {
    e.preventDefault();
    
    // Form field validaitons
    if (facilityType === 'Hall' && (!hallDate || !hallAttendance || !hallPurpose)) {
      alert("Please fill out all fields for the Church Hall booking.");
      return;
    }
    if (facilityType === 'Boardroom' && (!boardroomDate || !boardroomStartTime || !boardroomEndTime || !boardroomAgenda)) {
      alert("Please fill out all fields for the Boardroom booking.");
      return;
    }

    setIsSubmitting(true);
    const payload = facilityType === 'Hall' ? {
      facility: 'Church Hall',
      event_date: hallDate,
      attendance: parseInt(hallAttendance),
      purpose: hallPurpose
    } : {
      facility: 'Boardroom',
      event_date: boardroomDate,
      start_time: boardroomStartTime,
      end_time: boardroomEndTime,
      purpose: boardroomAgenda
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/book-church-facility', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          shop_id: shopId || userData?.shop_id || 68,
          member_name: userData?.username || userData?.name || 'Parish Member',
          member_id: userData?.id || 'Unknown',
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) throw new Error('Submission rejected');

      alert(`${facilityType} reservation request logged successfully!`);
      
      // Clear forms
      setHallDate(''); setHallAttendance(''); setHallPurpose('');
      setBoardroomDate(''); setBoardroomStartTime(''); setBoardroomEndTime(''); setBoardroomAgenda('');
      
      // Switch back to view list
      setBookingTab('current');
    } catch (error) {
      alert("Error dispatching booking request to reservation desk.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-white w-full rounded-[2.5rem] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col text-left transition-all duration-300 ${
        activeHtmlReport ? 'max-w-3xl' : 'max-w-md'
      }`}>
        
        {/* Header */}
        <div className="p-6 bg-slate-800 text-white flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            {activeHtmlReport && (
              <button 
                onClick={() => setActiveHtmlReport(null)}
                className="p-1.5 hover:bg-slate-700 rounded-lg mr-1 transition-colors"
                title="Go Back to Directory"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
                <FileText size={20} /> {activeHtmlReport ? 'Weekly Collection Report' : 'Church Services & Forms'}
              </h3>
              <p className="text-xs text-slate-300 uppercase font-medium mt-0.5">
                {activeHtmlReport ? 'Verified Parish Financial Ledger' : 'Parish Desk Document Directory'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-full transition-colors"><X size={20}/></button>
        </div>

        {/* Modal Main View Render Panel */}
        <div className="p-6 overflow-y-auto flex-1">
          {loadingReport ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3 text-slate-400">
              <Loader2 size={36} className="animate-spin text-blue-600" />
              <p className="text-xs font-black uppercase tracking-widest animate-pulse">
                Compiling collection matrix data...
              </p>
            </div>
          ) : activeHtmlReport ? (
            <div className="bg-white p-2 rounded-xl animate-in fade-in duration-200 html-report-wrapper">
              <iframe 
                srcDoc={activeHtmlReport} 
                title="Church Financial Statement Ledger"
                className="w-full min-h-[50vh] max-h-[60vh] border-0 rounded-xl"
                sandbox="allow-same-origin"
              />
            </div>
          ) : (
            /* Default List Grid View */
            <div className="space-y-3">
              {docs.map((doc, idx) => {
                const isTithesDoc = doc.title === 'Your Givings from the past week';
                const isBookingDoc = doc.title === 'Church Hall & Boardroom Booking';
                
                return (
                  <div 
                    key={idx}
                    onClick={() => {
                      if (isTithesDoc) {
                        handleViewTithesReport();
                      } else if (isBookingDoc) {
                        setIsBookingModalOpen(true);
                        setBookingTab('current');
                      } else {
                        alert(`Digital intake pipeline for "${doc.title}" coming soon to parish desk.`);
                      }
                    }}
                    className="p-4 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-between cursor-pointer hover:bg-blue-50/40 hover:border-blue-100 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-white border border-gray-100 text-slate-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        <doc.icon size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm">{doc.title}</h4>
                        <p className="text-[11px] text-gray-400 mt-0.5 font-medium">{doc.desc}</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-blue-600 uppercase bg-blue-50 px-2 py-1 rounded-md">Open</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* 🏛️ CHURCH FACILITY BOOKING LAYER MODAL 🏛️ */}
      {isBookingModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200 p-4">
          <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden text-left flex flex-col max-h-[85vh]">
      
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-700 to-indigo-800 p-6 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black tracking-wide">Church Facility Reservations</h3>
                <p className="text-xs opacity-80 font-medium">Manage and request allocations for church communal spaces</p>
              </div>
              <button 
                onClick={() => setIsBookingModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors text-white font-bold"
              >
                ✕
              </button>
            </div>

            {/* Tab Selectors */}
            <div className="flex border-b border-gray-100 bg-gray-50/50 p-2 gap-1">
              {[
                { id: 'current', label: 'Current Bookings' },
                { id: 'hall', label: 'Book the Church Hall' },
                { id: 'boardroom', label: 'Book the Boardroom' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setBookingTab(tab.id as any)}
                  className={`flex-1 py-2.5 text-xs font-black rounded-xl transition-all ${
                    bookingTab === tab.id 
                      ? 'bg-white text-blue-700 shadow-sm border border-gray-100' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Dynamic Content Panel Body Content */}
            <div className="p-6 flex-1 overflow-y-auto">
              
              {/* TAB 1: Current Bookings */}
              {bookingTab === 'current' && (
                <div className="space-y-3 animate-in fade-in duration-150">
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-2">Upcoming Schedule</h4>
                  {bookingsList.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed border-gray-100 rounded-2xl text-gray-400 text-xs font-medium">
                      No active reservations scheduled for this period.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {bookingsList.map((item, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 border border-gray-100 rounded-xl flex items-center justify-between">
                          <div>
                            <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded-md ${
                              item.facility?.includes('Hall') ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700'
                            }`}>
                              {item.facility || 'Facility'}
                            </span>
                            <h5 className="font-bold text-gray-900 text-xs mt-1">{item.purpose}</h5>
                            <p className="text-[10px] text-gray-400 font-medium mt-0.5">
                              Date: {item.event_date} {item.start_time ? `| ${item.start_time} - ${item.end_time}` : ''}
                            </p>
                          </div>
                          <span className="text-[10px] font-bold text-gray-500 bg-gray-200/60 px-2 py-0.5 rounded-full">
                            Scheduled
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* TAB 2: Book the Church Hall */}
              {bookingTab === 'hall' && (
                <form onSubmit={(e) => handleBookingSubmit(e, 'Hall')} className="space-y-4 animate-in fade-in duration-150">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Event Date</label>
                      <input 
                        type="date" 
                        value={hallDate}
                        onChange={(e) => setHallDate(e.target.value)}
                        className="w-full mt-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600" 
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Expected Attendance</label>
                      <input 
                        type="number" 
                        placeholder="e.g. 150" 
                        value={hallAttendance}
                        onChange={(e) => setHallAttendance(e.target.value)}
                        className="w-full mt-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Event Purpose / Description</label>
                    <textarea 
                      rows={3} 
                      placeholder="Provide details about the event or ministry gathering..." 
                      value={hallPurpose}
                      onChange={(e) => setHallPurpose(e.target.value)}
                      className="w-full mt-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600 resize-none" 
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-blue-600 text-white font-black rounded-xl text-xs tracking-wider shadow-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'PROCESSING REQUEST...' : 'SUBMIT CHURCH HALL REQUEST'}
                  </button>
                </form>
              )}

              {/* TAB 3: Book the Boardroom */}
              {bookingTab === 'boardroom' && (
                <form onSubmit={(e) => handleBookingSubmit(e, 'Boardroom')} className="space-y-4 animate-in fade-in duration-150">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Meeting Date</label>
                      <input 
                        type="date" 
                        value={boardroomDate}
                        onChange={(e) => setBoardroomDate(e.target.value)}
                        className="w-full mt-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Start Time</label>
                        <input 
                          type="time" 
                          value={boardroomStartTime}
                          onChange={(e) => setBoardroomStartTime(e.target.value)}
                          className="w-full mt-1 p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600" 
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">End Time</label>
                        <input 
                          type="time" 
                          value={boardroomEndTime}
                          onChange={(e) => setBoardroomEndTime(e.target.value)}
                          className="w-full mt-1 p-2.5 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600" 
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Committee / Meeting Agenda</label>
                    <textarea 
                      rows={3} 
                      placeholder="Specify board meeting purpose or leadership committee name..." 
                      value={boardroomAgenda}
                      onChange={(e) => setBoardroomAgenda(e.target.value)}
                      className="w-full mt-1 p-3 bg-gray-50 border border-gray-100 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-blue-600 resize-none" 
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full py-3 bg-indigo-600 text-white font-black rounded-xl text-xs tracking-wider shadow-md hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? 'PROCESSING REQUEST...' : 'SUBMIT BOARDROOM REQUEST'}
                  </button>
                </form>
              )}

            </div>
          </div>
        </div>
   
      )}

      </div>
    </div>
  );
};

const HeartIcon = () => (
  <svg xmlns="http://w3.org" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>
);
